import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux/store";
import { X, Save } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/card";
import { RootState } from "../../redux/store";
import { createChatRule, updateChatRule } from "../../redux/Slices/ChatRulesSlice";
import { ChatRule, ChatRuleTypeEnum, ChatRuleStatusEnum, ChatRuleCreateDto, ChatRuleUpdateDto } from "../../modules/shared/model";

interface ChatRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingRule?: ChatRule | null;
}

const ChatRuleModal: React.FC<ChatRuleModalProps> = ({ isOpen, onClose, editingRule = null }) => {
  const dispatch = useAppDispatch();
  const { loading } = useSelector((state: RootState) => state.chatRules);
  
  const [selectedType, setSelectedType] = useState<ChatRuleTypeEnum>(ChatRuleTypeEnum.No_Contact_Information);
  const [selectedStatus, setSelectedStatus] = useState<ChatRuleStatusEnum>(ChatRuleStatusEnum.Active);

  useEffect(() => {
    if (editingRule) {
      setSelectedType(editingRule.type.id);
      setSelectedStatus(editingRule.status.id);
    } else {
      setSelectedType(ChatRuleTypeEnum.No_Contact_Information);
      setSelectedStatus(ChatRuleStatusEnum.Active);
    }
  }, [editingRule]);

  const handleSave = async () => {
    const data: ChatRuleCreateDto | ChatRuleUpdateDto = {
      type: { id: selectedType },
      status: { id: selectedStatus }
    };

    if (editingRule) {
      dispatch(updateChatRule({ id: editingRule.id, data: data as ChatRuleUpdateDto }));
    } else {
      dispatch(createChatRule(data as ChatRuleCreateDto));
    }
    
    onClose();
  };

  const handleClose = () => {
    setSelectedType(ChatRuleTypeEnum.No_Contact_Information);
    setSelectedStatus(ChatRuleStatusEnum.Active);
    onClose();
  };

  const getRuleTypeDescription = (typeId: ChatRuleTypeEnum) => {
    const descriptions = {
      [ChatRuleTypeEnum.No_Contact_Information]: "blocks messages containing phone numbers, email addresses, or other external contact details.",
      [ChatRuleTypeEnum.Professional_Language]: "flags messages containing inappropriate or unprofessional language.",
      [ChatRuleTypeEnum.External_Platform_Links]: "blocks messages containing links to external platforms or services.",
      [ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter]: "block messages sent in rapid succession or containing spam (repeated keywords, suspicious content).",
      [ChatRuleTypeEnum.Sensitive_Content_Filter]: "detect and block words or phrases related to prohibited content (violence, discrimination, pornography).",
      [ChatRuleTypeEnum.Chat_Message_Limit]: "prevent flooding by limiting the number of messages sent within a defined time frame.",
      [ChatRuleTypeEnum.External_Deal_Detection]: "intercept messages attempting to move negotiations outside the platform (e.g., \"I'll pay you via PayPal\" or \"message me on Telegram\").",
      [ChatRuleTypeEnum.File_Attachment_Filter]: "block or restrict sending certain file types (e.g., executables, zip files, or potentially harmful content).",
      [ChatRuleTypeEnum.Role_Based_Contextual_Rules]: "apply different rules depending on whether the message is sent by a customer or by a professional."
    };
    return descriptions[typeId] || "Unknown rule type";
  };

  const getRuleTypeLabel = (typeId: ChatRuleTypeEnum) => {
    const labels = {
      [ChatRuleTypeEnum.No_Contact_Information]: "No Contact Information",
      [ChatRuleTypeEnum.Professional_Language]: "Professional Language",
      [ChatRuleTypeEnum.External_Platform_Links]: "External Platform Links",
      [ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter]: "Spam and Repetitive Messages Filter",
      [ChatRuleTypeEnum.Sensitive_Content_Filter]: "Sensitive Content Filter",
      [ChatRuleTypeEnum.Chat_Message_Limit]: "Chat Message Limit",
      [ChatRuleTypeEnum.External_Deal_Detection]: "External Deal Detection",
      [ChatRuleTypeEnum.File_Attachment_Filter]: "File Attachment Filter",
      [ChatRuleTypeEnum.Role_Based_Contextual_Rules]: "Role-Based Contextual Rules"
    };
    return labels[typeId] || "Unknown";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">{editingRule ? "Edit Chat Rule" : "Add New Chat Rule"}</h2>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rule Type
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(Number(e.target.value) as ChatRuleTypeEnum)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.values(ChatRuleTypeEnum).filter(value => typeof value === "number").map((typeId) => (
                <option key={typeId} value={typeId}>
                  {getRuleTypeLabel(typeId as ChatRuleTypeEnum)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {getRuleTypeDescription(selectedType)}
            </p>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(Number(e.target.value) as ChatRuleStatusEnum)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={ChatRuleStatusEnum.Active}>Active</option>
              <option value={ChatRuleStatusEnum.Inactive}>Inactive</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              {loading ? "Saving..." : (editingRule ? "Update Rule" : "Save Rule")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatRuleModal;
